<script setup>
/**
 * FormControlMProductsProductsTypeFeatures
 * Dynamically renders product variations/features matrix dropdown options.
 * Evaluates schemas out of backend database references when parent product types pivot,
 * reconciling modified entries safely to retain existing data while mapping additions.
 */
import {ref, computed, watch, onMounted} from 'vue'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
})

const itemDataStore = useItemDataStore()
const formStore = useFormStore(props.formId)

// --- State Layers ---

const type = ref(null)
const features = ref([])

// --- Synchronous Computeds ---

const typeId = computed(() => props.currentData?.typeId)

// --- Methods ---

/**
 * Parses comma-separated options text strings into structured operational arrays.
 * Example: 'Bar, Baz' => ['Bar', 'Baz']
 */
const parseOptions = (str) => {
    if (!str) return []
    return str.split(',').map(s => s.trim())
}

/**
 * Resets or hydrates the structural entity features definition data collection.
 */
const resetFeaturesDataset = async (setDefaultValue = false) => {
    if (!typeId.value) return

    try {
        const result = await itemDataStore.get({
            tableName: 'product_types',
            id: typeId.value
        })

        const freshFeatures = result?.features || []

        if (freshFeatures.length) {
            const templateDataArray = freshFeatures.map(feature => ({
                name: feature.name,
                value: ''
            }))

            if (setDefaultValue) {
                await formStore.setDefaultValue({
                    name: 'typeFeatures',
                    value: templateDataArray
                })
            } else {
                // Ensure reactivity is preserved on root property swap
                props.currentData[props.field.name] = templateDataArray
            }
        }
    } catch (err) {
        console.error('Failed to reset dynamic product features configuration layout:', err)
    }
}

/**
 * Monitors and processes features schema changes asynchronously.
 */
const syncFeaturesLayoutWithState = async () => {
    if (!typeId.value) {
        type.value = null
        features.value = []
        return
    }

    try {
        // Resolve primary category context
        const resolvedType = await itemDataStore.get({
            tableName: 'product_types',
            id: typeId.value
        })

        type.value = resolvedType
        const freshFeatures = resolvedType?.features || []

        const currentFieldArray = props.currentData?.[props.field.name] || []

        if (freshFeatures.length) {
            const targetNamesList = freshFeatures.map(o => o.name)
            const activeStoredNamesList = currentFieldArray.map(o => o.name)

            // Check if feature count or ordering has been altered upstream by admin panel
            if (
                targetNamesList.length !== activeStoredNamesList.length
                || JSON.stringify(targetNamesList) !== JSON.stringify(activeStoredNamesList)
            ) {
                const legacyCachedValues = [...currentFieldArray]

                // Clear structural data arrays down to defaults safely
                await resetFeaturesDataset(false)

                // Re-apply any historical criteria options matching identical feature text headings
                const updatedFieldArray = props.currentData?.[props.field.name] || []

                updatedFieldArray.forEach((nestedItem, targetIndex) => {
                    const match = legacyCachedValues.find(oldObj => oldObj.name === nestedItem.name)
                    if (match?.value) {
                        // Use explicit reactive setter to map historical selection bindings
                        props.currentData[props.field.name][targetIndex].value = match.value
                    }
                })
            } else {
                // TODO - This will never run because changing a name means the stringify check above will be true
                activeStoredNamesList.forEach((name, targetIndex) => {
                    if (name !== targetNamesList[targetIndex]) {
                        props.currentData[props.field.name][targetIndex].name = targetNamesList[targetIndex]
                    }
                })
            }
        }

        features.value = freshFeatures
    } catch (err) {
        console.error('Critical operational failure synchronizing features matrix catalog:', err)
    }
}

// --- Structural Watchers & Initializer ---

// Re-evaluate whenever the main type selection changes
watch(typeId, async () => {
    await syncFeaturesLayoutWithState()
}, {immediate: false})

onMounted(async () => {
    if (props.field) {
        props.field.label = ''
    }

    // Initialize root matrix arrays if null
    if (props.currentData && props.currentData[props.field.name] === null) {
        await resetFeaturesDataset(true)
    }

    // Run structural hydration mappings
    await syncFeaturesLayoutWithState()
})
</script>

<template>
    <div class="list-group form-control-products-type-features-wrapper">
        <div
            v-for="(feature, index) in features"
            :key="feature.name + '-' + index"
            class="list-group-item"
        >
            <div class="mb-1 small text-muted">{{ feature.name }}</div>

            <select
                v-if="currentData[field.name] && currentData[field.name][index]"
                v-model="currentData[field.name][index].value"
                class="form-select"
            >
                <option value="">Select&hellip;</option>
                <option
                    v-for="option in parseOptions(feature.options)"
                    :key="option"
                    :value="option"
                >
                    {{ option }}
                </option>
            </select>
        </div>
    </div>
</template>